import type { ReactElement } from 'react';
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Alert from 'atom-components/alert';
import RestrictedRoute from 'feature-components/routes/restricted-route';
import MarkResponsePage from 'pages/marking/subpages/mark-response-page';
import MarkerDashboardTab from 'pages/marking/subpages/marker-dashboard-tab';
import MarkingAllocationTab from 'pages/marking/subpages/marking-allocation-tab';
import MarkingGroupsTab from 'pages/marking/subpages/marking-groups-tab';
import MarkingGroupPage from 'pages/marking/subpages/marking-groups-tab/marking-group-page';
import MarksForReviewTab from 'pages/marking/subpages/marks-for-review-tab';
import { AuthorisableRoute } from 'policies/route.policy';
import { usePageAccess } from 'services/providers/page-access.provider';

export const markingPagePath = '/marking';

export enum MarkingPath {
  MarkerDashboard = `/marking/marker-dashboard`,
  MarkingAllocation = `/marking/marking-allocation`,
  MarksForReview = `/marking/marks-for-review`,
  MarkingGroups = `/marking/marking-groups`,
  NewMarkingGroup = `/marking/marking-groups/new`,
  MarkingGroup = `/marking/marking-groups/:groupId`,
  MarkResponse = `/marking/mark-response/:responseId`
}

/**
 * Converts a subpage path into a slug by removing the proceeding marking page
 * path and forward slash.
 */
function slug(path: MarkingPath): string {
  return path.substring(`${markingPagePath}/`.length);
}

export default function MarkingPageRoutes(): ReactElement {
  const { authorisableUser } = usePageAccess();

  // Prevent infinite loading spinner when loading a marking route if the user has not switched to
  // and joined an organisation.
  if (!authorisableUser.customerUser) {
    return (
      <Alert variant="danger">
        The marking section can only be accessed if you have switched to an organisation that you
        have joined.
      </Alert>
    );
  }

  return (
    <Routes>
      {/* Marking page tabs */}
      <Route element={<RestrictedRoute route={AuthorisableRoute.MarkingWildcard} />}>
        <Route path={slug(MarkingPath.MarkerDashboard)} element={<MarkerDashboardTab />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.MarkingAllocationPage} />}>
        <Route path={slug(MarkingPath.MarkingAllocation)} element={<MarkingAllocationTab />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.MarksForReviewPage} />}>
        <Route path={slug(MarkingPath.MarksForReview)} element={<MarksForReviewTab />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.MarkingGroupsWildcard} />}>
        <Route path={slug(MarkingPath.MarkingGroups)} element={<MarkingGroupsTab />} />
        <Route path={slug(MarkingPath.NewMarkingGroup)} element={<MarkingGroupPage />} />
        <Route path={slug(MarkingPath.MarkingGroup)} element={<MarkingGroupPage />} />
      </Route>

      {/* Mark response form */}
      <Route element={<RestrictedRoute route={AuthorisableRoute.MarkResponsePage} />}>
        <Route path={slug(MarkingPath.MarkResponse)} element={<MarkResponsePage />} />
      </Route>

      {/* If route not matched redirect to base route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
